import { supabase } from '@/integrations/supabase/client';

export interface PositionMapping {
  id: string;
  symbol: string;
  tv_to_ctrader_ratio: number;
  contract_size: number;
  base_unit: string;
  category: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePositionMappingData {
  symbol: string;
  tv_to_ctrader_ratio: number;
  contract_size?: number;
  base_unit: string;
  category?: string;
  is_active?: boolean;
}

export interface UpdatePositionMappingData extends Partial<CreatePositionMappingData> {
  id: string;
}

export class PositionMappingService {
  private static cache: Map<string, PositionMapping> = new Map();
  private static cacheExpiry: number = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all position mappings with caching
   */
  static async getAllMappings(): Promise<PositionMapping[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cache.size > 0 && now < this.cacheExpiry) {
      return Array.from(this.cache.values());
    }

    try {
      const { data, error } = await supabase
        .from('position_mappings')
        .select('*')
        .eq('is_active', true)
        .order('symbol');

      if (error) {
        console.error('Error fetching position mappings:', error);
        return [];
      }

      // Update cache
      this.cache.clear();
      data.forEach(mapping => {
        this.cache.set(mapping.symbol.toUpperCase(), mapping);
      });
      this.cacheExpiry = now + this.CACHE_TTL;

      return data;
    } catch (error) {
      console.error('Error in getAllMappings:', error);
      return [];
    }
  }

  /**
   * Get position mapping by symbol
   */
  static async getMappingBySymbol(symbol: string): Promise<PositionMapping | null> {
    const upperSymbol = symbol.toUpperCase();
    
    // Check cache first
    if (this.cache.has(upperSymbol) && Date.now() < this.cacheExpiry) {
      return this.cache.get(upperSymbol) || null;
    }

    // Refresh cache if needed
    await this.getAllMappings();
    return this.cache.get(upperSymbol) || null;
  }

  /**
   * Get TV to cTrader ratio for a symbol
   */
  static async getTVToCTraderRatio(symbol: string): Promise<number | null> {
    const mapping = await this.getMappingBySymbol(symbol);
    return mapping?.tv_to_ctrader_ratio || null;
  }

  /**
   * Get contract specifications for a symbol
   */
  static async getContractSpecs(symbol: string): Promise<{ contractSize: number; baseUnit: string } | null> {
    const mapping = await this.getMappingBySymbol(symbol);
    if (!mapping) return null;

    return {
      contractSize: mapping.contract_size,
      baseUnit: mapping.base_unit
    };
  }

  /**
   * Get available symbols
   */
  static async getAvailableSymbols(): Promise<string[]> {
    const mappings = await this.getAllMappings();
    return mappings.map(m => m.symbol);
  }

  /**
   * Create new position mapping (admin only)
   */
  static async createMapping(data: CreatePositionMappingData): Promise<PositionMapping | null> {
    try {
      const { data: mapping, error } = await supabase
        .from('position_mappings')
        .insert({
          symbol: data.symbol.toUpperCase(),
          tv_to_ctrader_ratio: data.tv_to_ctrader_ratio,
          contract_size: data.contract_size || 1,
          base_unit: data.base_unit,
          category: data.category || 'forex',
          is_active: data.is_active !== false
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating position mapping:', error);
        return null;
      }

      // Clear cache to force refresh
      this.clearCache();
      return mapping;
    } catch (error) {
      console.error('Error in createMapping:', error);
      return null;
    }
  }

  /**
   * Update position mapping (admin only)
   */
  static async updateMapping(data: UpdatePositionMappingData): Promise<PositionMapping | null> {
    try {
      const updateData: any = { ...data };
      if (updateData.symbol) {
        updateData.symbol = updateData.symbol.toUpperCase();
      }
      delete updateData.id;

      const { data: mapping, error } = await supabase
        .from('position_mappings')
        .update(updateData)
        .eq('id', data.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating position mapping:', error);
        return null;
      }

      // Clear cache to force refresh
      this.clearCache();
      return mapping;
    } catch (error) {
      console.error('Error in updateMapping:', error);
      return null;
    }
  }

  /**
   * Delete position mapping (admin only)
   */
  static async deleteMapping(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('position_mappings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting position mapping:', error);
        return false;
      }

      // Clear cache to force refresh
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Error in deleteMapping:', error);
      return false;
    }
  }

  /**
   * Bulk import mappings (admin only)
   */
  static async bulkImportMappings(mappings: CreatePositionMappingData[]): Promise<boolean> {
    try {
      const formattedMappings = mappings.map(mapping => ({
        symbol: mapping.symbol.toUpperCase(),
        tv_to_ctrader_ratio: mapping.tv_to_ctrader_ratio,
        contract_size: mapping.contract_size || 1,
        base_unit: mapping.base_unit,
        category: mapping.category || 'forex',
        is_active: mapping.is_active !== false
      }));

      const { error } = await supabase
        .from('position_mappings')
        .insert(formattedMappings);

      if (error) {
        console.error('Error bulk importing position mappings:', error);
        return false;
      }

      // Clear cache to force refresh
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Error in bulkImportMappings:', error);
      return false;
    }
  }

  /**
   * Clear cache
   */
  static clearCache(): void {
    this.cache.clear();
    this.cacheExpiry = 0;
  }

  /**
   * Get mappings by category
   */
  static async getMappingsByCategory(category: string): Promise<PositionMapping[]> {
    const mappings = await this.getAllMappings();
    return mappings.filter(m => m.category === category);
  }

  /**
   * Search mappings by symbol pattern
   */
  static async searchMappings(pattern: string): Promise<PositionMapping[]> {
    const mappings = await this.getAllMappings();
    const upperPattern = pattern.toUpperCase();
    return mappings.filter(m => m.symbol.includes(upperPattern));
  }
}